// ==============================================================================
// FILE: src/pages/ReviewQueuePage.tsx (FEATURE: Smart Dashboard Navigation)
// PURPOSE: Implements the logic to receive and apply one-time filters from
//          the dashboard for a seamless, actionable user experience.
//
// CHANGE 1: The `useAppContext` hook is used to retrieve `initialPageFilters`,
//           `clearInitialFilters`, and `setInitialFiltersAndNavigate`.
// CHANGE 2: A `useEffect` hook is added. When the component mounts, it checks
//           if `initialPageFilters` exists.
// CHANGE 3: If filters exist, they are applied to the page's local filter
//           state, and `clearInitialFilters` is immediately called to ensure
//           the action is only performed once. This preserves normal navigation.
// ==============================================================================
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Grid, TextField,
    FormControl, InputLabel, Select, MenuItem, Collapse, IconButton,
    Menu, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useAppContext } from '../contexts/AppContext';
import { ReviewQueueItem, User, ContractInfo } from '../types';
import {
    CheckCircleOutline as CheckCircleIcon,
    RateReview as RateReviewIcon,
    MoreVert as MoreVertIcon,
    Visibility as ViewIcon,
    FilterList as FilterListIcon,
    FilterListOff as FilterListOffIcon
} from '@mui/icons-material';

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 0,
}));

const formatClaimId = (id: number) => `C${String(id).padStart(5, '0')}`;

const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
}

const getSlaIndicatorProps = (submissionDate: string): { color: string; tooltip: string } | null => {
    const hoursDiff = (new Date().getTime() - new Date(submissionDate).getTime()) / (1000 * 60 * 60);
    if (hoursDiff > 48) {
        return { color: 'error.main', tooltip: 'SLA: Critical (> 48 hours)' };
    }
    if (hoursDiff > 24) {
        return { color: 'warning.main', tooltip: 'SLA: Aging (24-48 hours)' };
    }
    return null;
};

function CustomNoRowsOverlay() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
      <Typography variant="h6">The review queue is empty.</Typography>
      <Typography color="text.secondary">Great job!</Typography>
    </Box>
  );
}

export default function ReviewQueuePage(): JSX.Element {
  const { apiService, setActivePage, setReviewingBatchId, showNotification, initialPageFilters, clearInitialFilters } = useAppContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<ReviewQueueItem[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  const [filters, setFilters] = useState({
    employee_name: '',
    start_date: '',
    end_date: '',
    deputee_id: '',
    contractor_id: '',
    submitted_by_name: '',
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  const [users, setUsers] = useState<User[]>([]);
  const [contracts, setContracts] = useState<ContractInfo[]>([]);

  // --- NEW: Apply one-time filters from dashboard navigation ---
  useEffect(() => {
    if (initialPageFilters) {
        if (initialPageFilters.status) {
            // The review queue only shows 'review_required'
            // This is more relevant for the Claim History page but included for robustness
        }
        if (initialPageFilters.sla) {
            const now = new Date();
            let startDate = new Date();
            let endDate = new Date();

            if (initialPageFilters.sla === 'new') {
                startDate.setHours(now.getHours() - 24);
            } else if (initialPageFilters.sla === 'aging') {
                startDate.setHours(now.getHours() - 48);
                endDate.setHours(now.getHours() - 24);
            } else if (initialPageFilters.sla === 'critical') {
                endDate.setHours(now.getHours() - 48);
                // No start date for critical to get everything older
                setFilters(prev => ({ ...prev, end_date: endDate.toISOString().split('T')[0] }));
                clearInitialFilters();
                return;
            }
            
            setFilters(prev => ({
                ...prev,
                start_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0]
            }));
        }
        setShowFilters(true); // Show the filters panel when pre-filtered
        clearInitialFilters(); // Consume the filter so it's not reused
    }
  }, [initialPageFilters, clearInitialFilters]);


  useEffect(() => {
    apiService.getAllUsers().then(setUsers).catch(console.error);
    apiService.getContracts().then(setContracts).catch(console.error);
  }, [apiService]);

  const handleReviewClick = (batchId: string) => {
    setReviewingBatchId(batchId);
    setActivePage('Claim Review');
  };
  
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, batchId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedBatchId(batchId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBatchId(null);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name as string]: value }));
  };

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Needs Review">
          <IconButton size="small" onClick={() => handleReviewClick(params.row.ingestion_batch_id)}>
            <RateReviewIcon color="warning" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
        field: 'action',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<ReviewQueueItem>) => {
            const slaProps = getSlaIndicatorProps(params.row.submission_date);
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {slaProps && (
                        <Tooltip title={slaProps.tooltip}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: slaProps.color, mr: 1 }} />
                        </Tooltip>
                    )}
                    <Tooltip title="View Details">
                        <IconButton size="small" onClick={(e) => handleMenuClick(e, params.row.ingestion_batch_id)}>
                            <MoreVertIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        },
    },
    { 
      field: 'claim_sequence_id', 
      headerName: 'Claim ID', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => params.value ? formatClaimId(params.value) : 'N/A'
    },
    { field: 'deputee_id', headerName: 'Deputee ID', width: 120, renderCell: (params) => params.value || 'N/A' },
    { field: 'employee_name', headerName: 'Deputee Name', flex: 1, minWidth: 150, renderCell: (params) => params.value || 'N/A' },
    { field: 'contractor_id', headerName: 'Contract Code', width: 120, renderCell: (params) => params.value || 'N/A' },
    { field: 'client_name', headerName: 'Client Name', flex: 1, minWidth: 150, renderCell: (params) => params.value || 'N/A' },
    {
        field: 'claimed_amount',
        headerName: 'Claimed',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params: GridRenderCellParams) => formatCurrency(params.row.claimed_amount)
    },
    {
      field: 'submitted_by_name',
      headerName: 'Submitted By',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'submission_date',
      headerName: 'Submission Date',
      width: 180,
      valueGetter: (params: GridValueGetterParams<string>) =>
        params.value ? new Date(params.value).toLocaleString() : 'N/A',
    },
  ];

  useEffect(() => {
    apiService.getReviewQueue()
      .then(data => {
        setRows(data);
      })
      .catch((err) => showNotification(err.message || 'Failed to fetch review queue.', 'error'))
      .finally(() => setLoading(false));
  }, [apiService, showNotification]);

  const filteredRows = useMemo(() => {
    return rows.filter(row => {
        const submissionDate = new Date(row.submission_date);
        const startDate = filters.start_date ? new Date(filters.start_date) : null;
        const endDate = filters.end_date ? new Date(filters.end_date) : null;

        if (startDate && submissionDate < startDate) return false;
        if (endDate) {
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (submissionDate > endOfDay) return false;
        }
        if (filters.employee_name && !row.employee_name?.toLowerCase().includes(filters.employee_name.toLowerCase())) return false;
        if (filters.deputee_id && !row.deputee_id?.toLowerCase().includes(filters.deputee_id.toLowerCase())) return false;
        if (filters.contractor_id && row.contractor_id !== filters.contractor_id) return false;
        if (filters.submitted_by_name && (row as any).submitted_by_name !== filters.submitted_by_name) return false;
        return true;
    });
  }, [rows, filters]);
  
  const resetFilters = () => {
      setFilters({
          employee_name: '',
          start_date: '',
          end_date: '',
          deputee_id: '',
          contractor_id: '',
          submitted_by_name: '',
      });
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, height: 'calc(100vh - 110px)', width: '100%', display: 'flex', flexDirection: 'column' }} elevation={3}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>Review Queue</Typography>
            <Button startIcon={showFilters ? <FilterListOffIcon /> : <FilterListIcon />} onClick={() => setShowFilters(!showFilters)}>
                Filters
            </Button>
        </Box>
        <Collapse in={showFilters}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Deputee Name" name="employee_name" value={filters.employee_name} onChange={handleFilterChange} variant="outlined" size="small" /></Grid>
                    <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Deputee ID" name="deputee_id" value={filters.deputee_id} onChange={handleFilterChange} variant="outlined" size="small" /></Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Contract</InputLabel>
                            <Select name="contractor_id" value={filters.contractor_id} label="Contract" onChange={handleFilterChange as any}>
                                <MenuItem value=""><em>All</em></MenuItem>
                                {contracts.map(c => <MenuItem key={c.id} value={c.id}>{c.name} ({c.id})</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Submitted By</InputLabel>
                            <Select name="submitted_by_name" value={filters.submitted_by_name} label="Submitted By" onChange={handleFilterChange as any}>
                                <MenuItem value=""><em>All</em></MenuItem>
                                {users.map(u => <MenuItem key={u.id} value={u.display_name}>{u.display_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Start Date" name="start_date" type="date" value={filters.start_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                    <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="End Date" name="end_date" type="date" value={filters.end_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                    <Grid item xs={12} sm={6} md={3}><Button variant="outlined" onClick={resetFilters} fullWidth>Clear Filters</Button></Grid>
                </Grid>
            </Paper>
        </Collapse>
      </Box>
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <StyledDataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.ingestion_batch_id}
          loading={loading}
          initialState={{ pagination: { page: 0, pageSize: 10 } }}
          rowsPerPageOptions={[5, 10, 20]}
          disableSelectionOnClick
          components={{
            NoRowsOverlay: CustomNoRowsOverlay,
          }}
        />
      </Box>
       <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={() => { if(selectedBatchId) handleReviewClick(selectedBatchId); handleMenuClose(); }}>
            <ViewIcon sx={{mr: 1}}/> View Details
        </MenuItem>
      </Menu>
    </Paper>
  );
}

